import React, { useEffect, useState } from "react";
import {
  Trophy,
  Award,
  CheckCircle,
  Flame,
  Star,
  Zap,
  Users,
  Info,
  ChevronLeft,
  ChevronRight,
  UserPlus,
} from "lucide-react";
import { useGlobal } from "../context/GlobalContext";
import { images } from "../assets/images";

const LeaderBoard = () => {
  const { leaderBoard } = useGlobal();
  const [leaderboardData, setLeaderboardData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("rankings");

  const loadPage = async (page) => {
    setIsLoading(true);
    const data = await leaderBoard(page);
    setLeaderboardData(data || {});
    setIsLoading(false);
  };

  useEffect(() => {
    loadPage(currentPage);
  }, [currentPage]);

  const winners = (leaderboardData?.top_three || []).filter((item) =>
    item?.name?.trim(),
  );

  const restOfPlayers = (leaderboardData?.leader_board || []).filter((item) =>
    item?.name?.trim(),
  );

  const rewardCategories = leaderboardData?.rewards_category || [];
  const pagination = leaderboardData?.pagination || {};
  const totalPages = pagination.total_pages || 1;
  const perPage = pagination.per_page || 10;
  const totalRemaining = pagination.total_remaining || 0;
  const rangeStart = (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, totalRemaining);

  const pointsSystemData = rewardCategories.map((item, index) => {
    const icons = [
      <CheckCircle size={16} className="text-success" />,
      <Zap size={16} className="text-warning" />,
      <Flame size={16} className="text-danger" />,
      <Star size={16} className="text-info" />,
      <Users size={16} className="text-primary" />,
      <Award size={16} className="text-secondary" />,
    ];
    return {
      action: item.category_name,
      description: item.definition,
      points: `+${item.max_points}`,
      icon: icons[index % icons.length],
    };
  });

  // Build page number array with ellipsis
  const buildPages = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    const delta = 1; // siblings around current

    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    pages.push(totalPages);

    return pages;
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  // Placeholder shown on a podium slot that no one has claimed yet
  const EmptySeat = ({ size }) => (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center bg-light text-secondary shadow-sm"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        border: "2px dashed #cbd5e1",
      }}
    >
      <UserPlus size={size >= 90 ? 32 : 24} className="opacity-50" />
    </div>
  );

  return (
    <div className="content-part2">
      {/* Mobile-only tab bar */}
      <div className="d-flex d-md-none db-tabs mb-3">
        <button
          className={`db-tab ${activeTab === "rankings" ? "active" : ""}`}
          onClick={() => setActiveTab("rankings")}
        >
          <Users size={18} />
          Rankings
        </button>
        <button
          className={`db-tab ${activeTab === "earn" ? "active" : ""}`}
          onClick={() => setActiveTab("earn")}
        >
          <Award size={18} />
          Earn Points
        </button>
      </div>

      <div className="row">
        <div
          className={`col-md-8 ${activeTab === "rankings" ? "d-block" : "d-none d-md-block"}`}
        >
          {/* Podium */}
          <div className="podium-section p-3 rounded-4 bg-white shadow-sm border border-light mb-4">
            {winners.length === 0 ? (
              <div className="d-flex flex-column align-items-center justify-content-center text-center py-4 px-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-10 mb-3"
                  style={{ width: "72px", height: "72px" }}
                >
                  <Trophy size={32} className="text-warning" />
                </div>
                <h6 className="fw-bold mb-1 text-dark">No champions yet</h6>
                <p className="text-muted fs-13 mb-0">
                  Be the first to earn points and claim the top spot!
                </p>
              </div>
            ) : (
              <div className="d-flex justify-content-center align-items-end gap-2 gap-md-4 mb-2 pb-3">
                {/* 2nd Place */}
                <div className="podium-item text-center order-1 mt-4">
                  <div
                    className="position-relative mb-2 mx-auto"
                    style={{ width: "min-content" }}
                  >
                    {winners[1] ? (
                      <div
                        className="rounded-circle overflow-hidden border border-4 border-light shadow-sm"
                        style={{ width: "70px", height: "70px" }}
                      >
                        <img
                          src={winners[1]?.profile_img || images.userAvatar}
                          alt={winners[1]?.name}
                          className="w-100 h-100 object-fit-cover"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = images.userAvatar;
                          }}
                        />
                      </div>
                    ) : (
                      <EmptySeat size={70} />
                    )}
                    <div
                      className="position-absolute bottom-0 start-50 translate-middle-x bg-secondary text-white rounded-circle shadow-sm d-flex align-items-center justify-content-center fw-bold"
                      style={{
                        width: "22px",
                        height: "22px",
                        fontSize: "12px",
                        marginBottom: "-10px",
                      }}
                    >
                      2
                    </div>
                  </div>
                  {winners[1] ? (
                    <>
                      <h6
                        className="fw-bold mb-0 fs-13 text-truncate mt-3"
                        style={{ maxWidth: "100px" }}
                      >
                        {winners[1].name}
                      </h6>
                      <span className="text-muted fw-bold fs-13">
                        {winners[1].points || 0} pts
                      </span>
                    </>
                  ) : (
                    <>
                      <h6 className="fw-semibold mb-0 fs-13 text-muted mt-3">
                        Open spot
                      </h6>
                      <span className="text-muted fs-12">Up for grabs</span>
                    </>
                  )}
                </div>

                {/* 1st Place */}
                <div className="podium-item text-center order-2">
                  <div
                    className="position-relative mb-2 mx-auto"
                    style={{ width: "min-content" }}
                  >
                    <div
                      className="rounded-circle overflow-hidden border border-4 border-warning shadow-md"
                      style={{ width: "90px", height: "90px" }}
                    >
                      <img
                        src={winners[0]?.profile_img || images.userAvatar}
                        alt={winners[0]?.name}
                        className="w-100 h-100 object-fit-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = images.userAvatar;
                        }}
                      />
                    </div>
                    <Trophy
                      className="position-absolute top-0 end-0 text-warning"
                      size={24}
                      style={{
                        transform: "rotate(15deg) translate(5px, -5px)",
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                      }}
                    />
                    <div
                      className="position-absolute bottom-0 start-50 translate-middle-x bg-warning text-dark rounded-circle shadow-sm d-flex align-items-center justify-content-center fw-bold"
                      style={{
                        width: "28px",
                        height: "28px",
                        fontSize: "14px",
                        marginBottom: "-14px",
                      }}
                    >
                      1
                    </div>
                  </div>
                  <h6 className="fw-extrabold mb-0 fs-15 text-dark mt-3">
                    {winners[0]?.name || "-"}
                  </h6>
                  <span className="text-primary fw-bold fs-14">
                    {winners[0]?.points || 0} pts
                  </span>
                </div>

                {/* 3rd Place */}
                <div className="podium-item text-center order-3 mt-5">
                  <div
                    className="position-relative mb-2 mx-auto"
                    style={{ width: "min-content" }}
                  >
                    {winners[2] ? (
                      <div
                        className="rounded-circle overflow-hidden border border-4 border-light shadow-sm"
                        style={{ width: "70px", height: "70px" }}
                      >
                        <img
                          src={winners[2]?.profile_img || images.userAvatar}
                          alt={winners[2]?.name}
                          className="w-100 h-100 object-fit-cover"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = images.userAvatar;
                          }}
                        />
                      </div>
                    ) : (
                      <EmptySeat size={70} />
                    )}
                    <div
                      className="position-absolute bottom-0 start-50 translate-middle-x rounded-circle shadow-sm d-flex align-items-center justify-content-center fw-bold text-white"
                      style={{
                        width: "20px",
                        height: "20px",
                        fontSize: "11px",
                        marginBottom: "-10px",
                        backgroundColor: "#cd7f32",
                      }}
                    >
                      3
                    </div>
                  </div>
                  {winners[2] ? (
                    <>
                      <h6
                        className="fw-bold mb-0 fs-13 text-truncate mt-3"
                        style={{ maxWidth: "100px" }}
                      >
                        {winners[2].name}
                      </h6>
                      <span className="text-muted fw-bold fs-13">
                        {winners[2].points || 0} pts
                      </span>
                    </>
                  ) : (
                    <>
                      <h6 className="fw-semibold mb-0 fs-13 text-muted mt-3">
                        Open spot
                      </h6>
                      <span className="text-muted fs-12">Up for grabs</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Remaining Rankings */}
          <div className="content-card border-0 shadow-sm rounded-4 p-0 overflow-hidden">
            <div className="bg-secondary px-3 py-2 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span className="fw-bold text-white">Remaining Rankings</span>
              <span className="badge bg-light text-primary rounded-pill">
                {leaderboardData?.total_competitors} Competitors
              </span>
            </div>

            {isLoading ? (
              <div className="p-4 text-center text-muted fs-13">Loading...</div>
            ) : restOfPlayers.length === 0 ? (
              <div className="p-4 text-center text-muted fs-13">
                No rankings available.
              </div>
            ) : (
              restOfPlayers.map((item) => (
                <div
                  key={item.user_pid}
                  className={`d-flex align-items-center justify-content-between p-2 border-bottom transition-all hover-shadow bg-white ${item.is_current_user ? "is-you" : ""}`}
                >
                  <div className="d-flex align-items-center gap-3">
                    <span
                      className="fw-bold text-muted"
                      style={{ width: "25px" }}
                    >
                      #{item.rank}
                    </span>
                    <div
                      className="rounded-circle overflow-hidden border border-2 border-white shadow-sm flex-shrink-0"
                      style={{ width: "40px", height: "40px" }}
                    >
                      <img
                        src={item.profile_img || images.userAvatar}
                        alt={item.name}
                        className="w-100 h-100 object-fit-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = images.userAvatar;
                        }}
                      />
                    </div>
                    <span
                      className={`fs-14 ${item.is_current_user ? "fw-bold text-primary" : "fw-medium text-dark"}`}
                    >
                      {item.name}
                    </span>
                  </div>
                  <span className="fw-bold text-primary fs-14">
                    {item.points} pts
                  </span>
                </div>
              ))
            )}

            {/* Pagination footer */}
            {totalRemaining > 0 && (
              <div className="lb-pagination">
                <span className="lb-range-text">
                  {rangeStart}–{rangeEnd} of {totalRemaining}
                </span>

                {totalPages > 1 && (
                  <div className="lb-pages">
                    <button
                      className="lb-page-btn lb-page-nav"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={15} />
                    </button>

                    {buildPages().map((page, idx) =>
                      page === "..." ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="lb-page-ellipsis"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          className={`lb-page-btn${currentPage === page ? " active" : ""}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ),
                    )}

                    <button
                      className="lb-page-btn lb-page-nav"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                    >
                      <ChevronRight size={15} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Earn Points sidebar */}
        <div
          className={`col-md-4 ${activeTab === "earn" ? "d-block" : "d-none d-md-block"}`}
        >
          <div className="content-card shadow-sm border-0 rounded-4 p-0">
            <div className="bg-secondary px-3 py-2 text-white rounded-top-4">
              <div className="d-flex align-items-center gap-2">
                <Award size={20} />
                <h6 className="mb-0 fw-bold">Earn Points</h6>
              </div>
            </div>
            <div className="px-3 py-2 bg-white">
              {pointsSystemData.map((item, index) => (
                <div
                  key={index}
                  className="d-flex align-items-center justify-content-between py-2 border-bottom last-child-border-0"
                >
                  <div className="d-flex align-items-center gap-2">
                    {item.icon}
                    <div>
                      <div className="fs-13 fw-semibold text-dark">
                        {item.action}
                      </div>
                      <div className="fs-12 text-muted">{item.description}</div>
                    </div>
                  </div>
                  <span className="badge bg-success bg-opacity-10 text-success fw-bold p-2">
                    {item.points}
                  </span>
                </div>
              ))}

              <div className="mt-4 p-3 rounded-3 bg-light border border-light">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Info size={16} className="text-primary" />
                  <span className="fw-bold fs-13">Pro Tip</span>
                </div>
                <p className="mb-0 fs-13 text-muted">
                  Complete daily check-ins and exceed targets consistently to
                  unlock premium rewards!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderBoard;
